import { useState } from 'react';
import { Link } from 'react-router';
import { useAccountsStore, useStatusStore } from '@lib/store';
import { cn, RoutePaths } from '@shared/utils';
import { RecipientTypeEnum, type GetRequisitesResponse } from '@shared/api';
import { AddNewRecipientModal, SelectRecipientModal, TopUpMyAccountModal } from '@widgets';
import { Tab } from './tab';
import { ExternalPaymentsModal, TransferBetweenMyAccountModal, TransferToCardModal } from './modals';
import { Button } from '@shared/widgets';

interface PaymentsProps {
  className?: string;
  paymentsTab?: boolean;
}

export const Payments = (props: PaymentsProps) => {
  const accountsStore = useAccountsStore();
  const statusStore = useStatusStore();

  const [recipient, setRecipient] = useState<GetRequisitesResponse | undefined>();
  const [recipientType, setRecipientType] = useState<RecipientTypeEnum>(RecipientTypeEnum.Add);
  const [showTransferToCardModal, setShowTransferToCardModal] = useState<boolean>(false);
  const [showExternalPaymentsModal, setShowExternalPaymentsModal] = useState<boolean>(false);
  const [showSelectRecipientModal, setShowSelectRecipientModal] = useState<boolean>(false);
  const [showAddNewRecipientModal, setShowAddNewRecipientModal] = useState<boolean>(false);
  const [showTransferBetweenMyAccounts, setShowTransferBetweenMyAccounts] = useState<boolean>(false);
  const [showTopMyAccount, setShowTopMyAccount] = useState<boolean>(false);

  return (
    <>
      {props.paymentsTab && (
        <div className={cn('border-y border-y-[#97815e33]', 'max-md:-mx-5', props.className)}>
          <ul className={cn('flex h-[70px] w-full items-center justify-between overflow-x-auto whitespace-nowrap', 'max-md:px-5')}>
            <Tab className={cn('mr-[0.5%] border-b border-b-[currentColor]', 'max-md:mx-5 max-md:ml-0')} label='Overview' />
            <Tab className={cn('mx-[0.5%] opacity-50', 'max-md:mx-5')} label='Transfer to Card' onClick={() => setShowTransferToCardModal(true)} />
            <Tab className={cn('mx-[0.5%] opacity-50', 'max-md:mx-5')} label='Top up my accounts' onClick={() => setShowTopMyAccount(true)} />
            <Tab
              className={cn('mx-[0.5%] opacity-50', 'max-md:mx-5')}
              label='Internal transfer'
              onClick={() => setShowTransferBetweenMyAccounts(true)}
            />
            <Tab
              className={cn(
                'ml-[0.5%] opacity-50',
                statusStore.data?.OperationalStatus === 'NonOperational' && 'pointer-events-none text-[#f2f2f24d]',
                'max-md:ml-5',
              )}
              label='External transfer'
              onClick={() => setShowExternalPaymentsModal(true)}
            />
          </ul>
        </div>
      )}
      {!props.paymentsTab && (
        <div className={cn(props.className)}>
          <div
            className={cn(
              'mb-6.5 flex h-[20px] items-center justify-between pl-3 select-none',
              'max-md:mb-5 max-md:h-[15px] max-md:p-0 max-md:pl-2.5',
            )}
          >
            <div
              className={cn(
                'relative text-xl font-bold text-[#f2f2f2] uppercase',
                'before:absolute before:top-0 before:left-[-10px] before:font-semibold before:text-[#a73f33] before:content-["/"]',
                'max-md:text-sm',
              )}
            >
              Payments
            </div>
          </div>
          <div className={cn('grid grid-cols-2 items-center justify-center gap-2', 'max-md:flex max-md:flex-col max-md:flex-wrap')}>
            <Button className='h-[65px]' fullWidth onClick={() => setShowTransferToCardModal(true)}>
              Transfer to card
            </Button>
            <Button className='h-[65px]' fullWidth onClick={() => setShowTransferBetweenMyAccounts(true)}>
              Transfer between my accounts
            </Button>
            <Button
              className='h-[65px]'
              fullWidth
              disabled={statusStore.data?.OperationalStatus === 'NonOperational'}
              onClick={() => setShowExternalPaymentsModal(true)}
            >
              External payments
            </Button>
            <Link to={RoutePaths.payments.onNavigate()}>
              <Button className='h-[65px]' fullWidth>
                Payments overview
              </Button>
            </Link>
          </div>
        </div>
      )}
      <TransferToCardModal isOpen={showTransferToCardModal} onClose={() => setShowTransferToCardModal(false)} />
      {props.paymentsTab && (
        <TopUpMyAccountModal isOpen={showTopMyAccount} onClose={() => setShowTopMyAccount(false)} account={accountsStore.data?.[0]} />
      )}
      <TransferBetweenMyAccountModal isOpen={showTransferBetweenMyAccounts} onClose={() => setShowTransferBetweenMyAccounts(false)} />
      <ExternalPaymentsModal
        isOpen={showExternalPaymentsModal}
        onClose={() => setShowExternalPaymentsModal(false)}
        onShowSelectAccountModal={setShowSelectRecipientModal}
        selectedRecipient={recipient}
      />
      <SelectRecipientModal
        isOpen={showSelectRecipientModal}
        onClose={() => setShowSelectRecipientModal(false)}
        onShowAddNewRecipientModal={() => setShowAddNewRecipientModal(true)}
        onShowExternalPaymentModal={() => setShowExternalPaymentsModal(true)}
        onChangeRecipient={setRecipient}
        selectedRecipient={recipient}
        onChangeRecipientType={setRecipientType}
      />
      <AddNewRecipientModal
        isOpen={showAddNewRecipientModal}
        onClose={() => setShowAddNewRecipientModal(false)}
        recipientType={recipientType}
        selectedRecipient={recipient}
        onShowSelectRecipientModal={() => setShowSelectRecipientModal(true)}
      />
    </>
  );
};
